// @ts-check
import _ from 'lodash';
import { camelize } from 'humps';

// Takes a string like "Has the patient had surgery?"
// and returns "hasThePatientHadSurgery"
export function cleanCamelHash(prompt) {
  if (prompt) {
    return _.camelCase(prompt).replace(/[^a-zA-Z0-9]+/g, '');
  }
  return prompt;
}

/**
 * @param {string} sectionTitle
 * @param {string} prompt
 * @returns {string}
 */
export function formFieldName(sectionTitle, prompt) {
  const camelizedSectionTitle = cleanCamelHash(sectionTitle);
  const camelizedPrompt = cleanCamelHash(prompt);
  return `${camelizedSectionTitle}__${camelizedPrompt}`;
}

// TODO: yes/no/explain?
const ElementTypes = {
  checkbox: 'checkbox',
  radio: 'radio',
  select: 'select',
  textArea: 'text_area',
  textInput: 'text_input',
};

const CommonOperators = {
  is: { label: 'is', value: 'is' },
  isNot: { label: 'is not', value: 'is_not' },
  isPredicate: { label: 'is', value: 'is_predicate' },
  isNotPredicate: { label: 'is not', value: 'is_not_predicate' },
  isLessThan: { label: 'is less than', value: 'is_less_than' },
  isGreaterThan: { label: 'is greater than', value: 'is_greater_than' },
  contains: { label: 'contains', value: 'contains' },
  doesNotContain: { label: 'does not contain', value: 'does_not_contain' },
  beginsWith: { label: 'begins with', value: 'begins_with' },
  endsWith: { label: 'ends with', value: 'ends_with' },
};

export function operatorsForElementType(elementType) {
  switch (elementType) {
    case ElementTypes.checkbox:
      return [
        CommonOperators.is,
        CommonOperators.isNot,
        CommonOperators.contains,
        CommonOperators.doesNotContain,
      ];

    case ElementTypes.radio:
    case ElementTypes.select:
    case ElementTypes.textArea:
      return [
        CommonOperators.is,
        CommonOperators.isNot,
        CommonOperators.contains,
        CommonOperators.doesNotContain,
        CommonOperators.beginsWith,
        CommonOperators.endsWith,
      ];

    case ElementTypes.textInput:
      return [
        CommonOperators.is,
        CommonOperators.isNot,
        CommonOperators.isGreaterThan,
        CommonOperators.isLessThan,
        CommonOperators.contains,
        CommonOperators.doesNotContain,
        CommonOperators.beginsWith,
        CommonOperators.endsWith,
      ];

    default:
      return [];
  }
}

/**
 * @param {Array<any> | void} maybeArray
 * @returns {boolean}
 */
function isBlank(maybeArray) {
  return !(Array.isArray(maybeArray) && maybeArray.length > 0);
}

function answerOptionsToPossibleValues(answerOptions) {
  return isBlank(answerOptions)
    ? null
    : answerOptions.map(ao => ({
        value: ao.value,
        text: ao.value,
      }));
}

/** @typedef {{
  required: boolean,
  prompt: string,
  elementType: "text_area" | "text_input" | "checkbox" | "radio" | "select"
  answerOptions: Array<string> | void
}} Question */

/** @typedef {{
  title: string,
  suppressNotes: true,
  questions: Array<Question>
}} Section */

/**
 * @param {Array<Section>} sections
 * @returns {Array<{sectionTitle:string, question:Question}>}
 */
export function questionnaireQuestions(sections) {
  return sections
    .map(s => s.questions.map(q => ({ sectionTitle: s.title, question: q })))
    .reduce((acc, elem) => acc.concat(elem), []);
}

/** @typedef {{
  componentType: "textbox" | "plainselect",
  displayName: string,
  identifier: string,
  longDisplayName: string,
  operators: Array<{value:string, label:string}>,
  possibleValues: Array<string>,
  groupName: string,
}} Rulable */

/**
 * @param {{sectionTitle:string, question:Question}} questionBundle
 * @returns {Rulable}
 */
export function questionAsRulable(questionBundle) {
  const { sectionTitle, question } = questionBundle;
  return {
    componentType: isBlank(question.answerOptions) ? 'textbox' : 'plainselect',
    displayName: `${sectionTitle}/${question.prompt}`,
    identifier: `questionnaire--${sectionTitle}__${question.prompt}`,
    longDisplayName: `Questionnaire Answer for ${sectionTitle}/${question.prompt}`,
    operators: operatorsForElementType(question.elementType),
    possibleValues: answerOptionsToPossibleValues(question.answerOptions),
    groupName: `Questionnaire/${sectionTitle}`,
  };
}

function valueForRulableField(allAnswers, rulableReferralProfileValues, rulableFieldIdentifier) {
  if (rulableFieldIdentifier.substr(0, 12) === "Referral's--") {
    const attribute = rulableFieldIdentifier.substr(12);
    return rulableReferralProfileValues[camelize(attribute)];
  }

  if (rulableFieldIdentifier.substr(0, 15) === 'questionnaire--') {
    const attribute = rulableFieldIdentifier.substr(15);
    // HACK:
    // TODO: This feels very hacky. There's an unfotunate difference between
    //       the ruby and js. Forms value keys can't have spaces, so gotta do this manually.
    const [sectionTitle, prompt] = attribute.split('__');
    return allAnswers[formFieldName(sectionTitle, prompt)];
  }

  throw new Error(`Invalid rulableFieldIdentifier: ${rulableFieldIdentifier}`);
}

function evaluateConditional(conditional, allAnswers, rulableReferralProfileValues) {
  const { operator, rulableFieldIdentifier, compareValue } = conditional;

  const actualValue = valueForRulableField(
    allAnswers,
    rulableReferralProfileValues,
    rulableFieldIdentifier
  );

  if (actualValue === undefined || actualValue === null) {
    return false;
  }

  switch (operator) {
    case '==':
    case 'is_equal_to':
    case 'is':
      return actualValue == compareValue; // eslint-disable-line eqeqeq

    case '!=':
    case 'is_not_equal_to':
    case 'is_not':
      return actualValue != compareValue; // eslint-disable-line eqeqeq

    case '>':
    case 'is_greater_than':
      return actualValue > compareValue;

    case '<':
    case 'is_less_than':
      return actualValue < compareValue;

    case '>=':
      return actualValue >= compareValue;

    case '<=':
      return actualValue <= compareValue;

    case 'contains':
    case 'includes':
      return actualValue.indexOf(compareValue) !== -1;

    case 'does_not_contain':
    case 'does_not_include':
      return actualValue.indexOf(compareValue) === -1;

    case 'begins_with':
      return actualValue.startsWith(compareValue);

    case 'ends_with':
      return actualValue.endsWith(compareValue);

    case 'is_predicate':
      return actualValue[camelize(compareValue)];

    case 'is_not_predicate':
      return !actualValue[camelize(compareValue)];

    default:
      return false;
    // throw new Error(`Invalid conditional operator: ${operator}`);
  }
}

function isSatisfiedBy(conditionalItem, allAnswers, rulableReferralProfileValues) {
  const { operator, children, conditionals } = conditionalItem;
  const conditionalItems = (children || []).concat(conditionals || []);
  switch (operator) {
    case 'AND':
      return conditionalItems.reduce(
        (acc, item) => acc && isSatisfiedBy(item, allAnswers, rulableReferralProfileValues),
        true
      );

    case 'OR':
      return conditionalItems.reduce(
        (acc, item) => acc || isSatisfiedBy(item, allAnswers, rulableReferralProfileValues),
        false
      );

    default:
      // It must be a conditional rather than a conditional group.
      return evaluateConditional(conditionalItem, allAnswers, rulableReferralProfileValues);
  }
}

function shouldShowFor(allAnswers = {}, rulableReferralProfileValues) {
  return conditionedItem => {
    const { defaultVisible, visibilityConditionalGroup, visibilityThenAction } = conditionedItem;
    const hasConditional = !!visibilityConditionalGroup;

    if (
      hasConditional &&
      isSatisfiedBy(visibilityConditionalGroup, allAnswers, rulableReferralProfileValues)
    ) {
      switch (visibilityThenAction) {
        case 'makeVisible':
          return true;
        case 'makeHidden':
          return false;
        default:
          return defaultVisible;
      }
    } else {
      return defaultVisible;
    }
  };
}

export function updateVisible(questionnaire, allAnswers, rulableReferralProfileValues) {
  const shouldShow = shouldShowFor(allAnswers, rulableReferralProfileValues);
  const visibleSections = questionnaire.sections.filter(shouldShow);

  const visibleQuestionnaire = {
    ...questionnaire,
    sections: visibleSections.map(section => ({
      ...section,
      questions: section.questions.map(question => {
        // HACK: Doing this to fix the "chain rule" problem. See PRF-576.
        //       We're going to "delete" answers to questions that shouldn't be visible,
        //       so that answered hidden questions can't trigger other hidden questions to
        //       become visible.
        const answerKey = formFieldName(section.title, question.prompt);
        if (allAnswers && allAnswers[answerKey] && !shouldShow(question)) {
          delete allAnswers[answerKey];
        }
        return question;
      }).filter(shouldShow),
    })),
  };
  return visibleQuestionnaire;
}
